/** Parses the JS object literals merchants paste into the Spark Options import modal (theme JS, not JSON). Unreadable values are stepped over, dropped, and reported to any {@link ImportWarnings}. */

import { childPath, type ImportWarnings } from './warnings';

/** Stands in for a value the parser stepped over but cannot represent as data. */
const UNSUPPORTED = Symbol('unsupported');

type Unsupported = typeof UNSUPPORTED;

const IDENTIFIER_START = /[A-Za-z_$]/;
const IDENTIFIER_PART = /[\w$]/;
const WHITESPACE = /\s/;
const NUMBER =
  /[+-]?(?:0[xX][\da-fA-F]+|0[bB][01]+|0[oO][0-7]+|(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?)/y;

const KEYWORDS = new Map<string, unknown>([
  ['true', true],
  ['false', false],
  ['null', null],
  ['NaN', Number.NaN],
  ['Infinity', Number.POSITIVE_INFINITY],
  // Dropped rather than stored, so it never overwrites an existing value.
  ['undefined', UNSUPPORTED],
]);

const STRING_ESCAPES: Record<string, string> = {
  b: '\b',
  f: '\f',
  n: '\n',
  r: '\r',
  t: '\t',
  v: '\v',
  '0': '\0',
};

/** Characters after which a `/` opens a regex rather than divides (crude; only used while skipping). */
const REGEX_CAN_FOLLOW = new Set([
  '',
  '(',
  ')',
  '[',
  ']',
  '{',
  '}',
  ',',
  ';',
  ':',
  '=',
  '!',
  '&',
  '|',
  '?',
  '+',
  '-',
  '*',
  '%',
  '~',
  '^',
  '<',
  '>',
]);

/** Characters that end a value: everything else belongs to a larger expression. */
const VALUE_TERMINATORS = new Set(['', ',', '}', ']']);

class JsObjectScanner {
  private index = 0;

  constructor(
    private readonly source: string,
    private readonly warnings?: ImportWarnings,
  ) {}

  /** Read the first object literal in the source, ignoring anything around it. */
  read(): Record<string, unknown> {
    this.seekObjectStart();
    return this.readObject('');
  }

  /** Character at the cursor, or `''` past the end of the source. */
  private peek(offset = 0): string {
    return this.source.charAt(this.index + offset);
  }

  private atEnd(): boolean {
    return this.index >= this.source.length;
  }

  /** Skip whitespace, line and block comments, and Liquid statement tags. */
  private skipTrivia(): void {
    while (!this.atEnd()) {
      const char = this.peek();

      if (WHITESPACE.test(char)) {
        this.index += 1;
        continue;
      }

      if (char === '/' && this.peek(1) === '/') {
        const end = this.source.indexOf('\n', this.index);
        this.index = end === -1 ? this.source.length : end + 1;
        continue;
      }

      if (char === '/' && this.peek(1) === '*') {
        const end = this.source.indexOf('*/', this.index + 2);
        this.index = end === -1 ? this.source.length : end + 2;
        continue;
      }

      if (char === '{' && this.peek(1) === '%') {
        this.skipLiquidStatement();
        continue;
      }

      return;
    }
  }

  /** Step over a `{% … %}` tag; `{% comment %}` blocks take their body (those members were commented out). */
  private skipLiquidStatement(): void {
    let depth = this.readLiquidStatement() === 'comment' ? 1 : 0;

    while (depth > 0) {
      const next = this.source.indexOf('{%', this.index);
      if (next === -1) break;

      this.index = next;
      const tag = this.readLiquidStatement();
      if (tag === 'comment') depth += 1;
      else if (tag === 'endcomment') depth -= 1;
    }

    if (depth > 0) {
      this.index = this.source.length; // unterminated
    }
  }

  /** Consume a `{% … %}` tag and return its name (`comment`, `endif`, …). */
  private readLiquidStatement(): string {
    const start = this.index + 2;
    const end = this.source.indexOf('%}', start);
    const body = end === -1 ? this.source.slice(start) : this.source.slice(start, end);
    this.index = end === -1 ? this.source.length : end + 2;

    // `{%- … -%}` trims surrounding whitespace; the name is unaffected.
    return body.trim().replace(/^-|-$/g, '').trim().split(/\s+/)[0] ?? '';
  }

  /** Consume a `{{ … }}` output tag. */
  private skipLiquidOutput(): void {
    const end = this.source.indexOf('}}', this.index + 2);
    this.index = end === -1 ? this.source.length : end + 2;
  }

  /** Move the cursor to the `{` that opens the options object. */
  private seekObjectStart(): void {
    while (!this.atEnd()) {
      this.skipTrivia();
      const char = this.peek();

      if (char === '{') return;
      if (char === '"' || char === "'" || char === '`') {
        this.skipQuoted(char);
        continue;
      }

      this.index += 1;
    }

    throw new Error('Could not find an options object in the snippet.');
  }

  private readObject(path: string): Record<string, unknown> {
    const result: Record<string, unknown> = {};
    this.index += 1; // '{'

    while (!this.atEnd()) {
      this.skipTrivia();
      const char = this.peek();

      if (char === '}') {
        this.index += 1;
        return result;
      }
      // Separators, trailing commas and commas left by a skipped member.
      if (char === ',') {
        this.index += 1;
        continue;
      }
      if (char === '') break;
      // A `{{ … }}` where a key belongs injects members we can't see; step over just the tag.
      if (char === '{' && this.peek(1) === '{') {
        this.skipLiquidOutput();
        continue;
      }

      const start = this.index;
      const key = this.readKey();
      this.skipTrivia();

      if (key === null || this.peek() !== ':') {
        // Shorthand, methods, spreads and computed keys carry nothing storable — skip the member.
        if (key !== null) this.warnings?.add(childPath(path, key));
        this.skipExpression();
      } else {
        this.index += 1; // ':'
        const valuePath = childPath(path, key);
        const value = this.readValue(valuePath);
        if (value === UNSUPPORTED) {
          this.warnings?.add(valuePath);
        } else if (key !== '__proto__') {
          // `__proto__` would rewrite the prototype instead of adding a key.
          result[key] = value;
        }
      }

      this.ensureProgress(start, '}');
    }

    throw new Error('Unterminated object in the snippet.');
  }

  private readArray(path: string): unknown[] {
    const result: unknown[] = [];
    this.index += 1; // '['
    let itemIndex = 0;

    while (!this.atEnd()) {
      this.skipTrivia();
      const char = this.peek();

      if (char === ']') {
        this.index += 1;
        return result;
      }
      // Separators, trailing commas and holes.
      if (char === ',') {
        this.index += 1;
        itemIndex += 1;
        continue;
      }
      if (char === '') break;

      const start = this.index;
      const itemPath = childPath(path, itemIndex);
      const value = this.readValue(itemPath);
      if (value === UNSUPPORTED) {
        this.warnings?.add(itemPath);
      } else {
        result.push(value);
      }

      this.ensureProgress(start, ']');
    }

    throw new Error('Unterminated array in the snippet.');
  }

  /** Force the cursor forward when a member read nothing, so malformed input can't spin. */
  private ensureProgress(start: number, closer: string): void {
    if (this.index !== start) return;

    const char = this.peek();
    if (char !== ',' && char !== closer) {
      this.index += 1;
    }
  }

  /** Read a property name, or return `null` when the member has no plain key. */
  private readKey(): string | null {
    const char = this.peek();

    if (char === '"' || char === "'") {
      const key = this.readString(char);
      return key === UNSUPPORTED ? null : key;
    }

    if (IDENTIFIER_START.test(char)) {
      return this.readIdentifier();
    }

    return this.readNumber()?.toString() ?? null;
  }

  private readValue(path: string): unknown {
    const start = this.index;
    const value = this.readValueStart(path);

    if (value === UNSUPPORTED) return UNSUPPORTED;

    this.skipTrivia();
    if (VALUE_TERMINATORS.has(this.peek())) return value;

    // Only the head of an expression (e.g. `"/" + shop.url`), not a standalone literal.
    this.index = start;
    this.skipExpression();
    return UNSUPPORTED;
  }

  private readValueStart(path: string): unknown {
    this.skipTrivia();
    const char = this.peek();

    // `{{ … }}` is a Liquid output tag, not an object.
    if (char === '{' && this.peek(1) !== '{') {
      return this.readObject(path);
    }
    if (char === '[') {
      return this.readArray(path);
    }
    if (char === '"' || char === "'" || char === '`') {
      return this.readString(char);
    }
    if (IDENTIFIER_START.test(char)) {
      const start = this.index;
      const word = this.readIdentifier();
      if (KEYWORDS.has(word)) {
        return KEYWORDS.get(word);
      }
      // An identifier, `function`, `new Foo()`, `async () => {}`…
      this.index = start;
      this.skipExpression();
      return UNSUPPORTED;
    }

    const number = this.readNumber();
    if (number !== null) {
      return number;
    }

    this.skipExpression();
    return UNSUPPORTED;
  }

  private readIdentifier(): string {
    const start = this.index;
    while (!this.atEnd() && IDENTIFIER_PART.test(this.peek())) {
      this.index += 1;
    }
    return this.source.slice(start, this.index);
  }

  private readNumber(): number | null {
    NUMBER.lastIndex = this.index;
    const match = NUMBER.exec(this.source);
    if (!match) return null;

    this.index += match[0].length;
    // `Number('-0x10')` is NaN, so apply the sign separately.
    const sign = match[0].startsWith('-') ? -1 : 1;
    return sign * Number(match[0].replace(/^[+-]/, ''));
  }

  private readString(quote: string): string | Unsupported {
    const start = this.index;
    this.index += 1;
    let value = '';

    while (!this.atEnd()) {
      const char = this.peek();
      this.index += 1;

      if (char === quote) return value;

      if (char === '\\') {
        value += this.readEscape();
        continue;
      }

      // A template literal with substitutions is only known at runtime.
      if (quote === '`' && char === '$' && this.peek() === '{') {
        this.index = start;
        this.skipExpression();
        return UNSUPPORTED;
      }

      // Liquid templates the string at runtime wherever it sits, so the literal text isn't the value.
      if (char === '{' && (this.peek() === '{' || this.peek() === '%')) {
        this.index = start;
        this.skipExpression();
        return UNSUPPORTED;
      }

      value += char;
    }

    throw new Error('Unterminated string in the snippet.');
  }

  private readEscape(): string {
    const char = this.peek();
    this.index += 1;

    if (char === '' || char === '\n') return ''; // line continuation
    if (char === 'x') return this.readCodePoint(2);
    if (char !== 'u') return STRING_ESCAPES[char] ?? char;

    if (this.peek() !== '{') return this.readCodePoint(4);

    const end = this.source.indexOf('}', this.index);
    if (end === -1) return '';
    const digits = this.source.slice(this.index + 1, end);
    this.index = end + 1;
    return toCodePoint(digits);
  }

  private readCodePoint(length: number): string {
    const digits = this.source.slice(this.index, this.index + length);
    if (digits.length < length) return '';
    this.index += length;
    return toCodePoint(digits);
  }

  /** Step over an expression, leaving the cursor on the punctuation that closes the object/array. */
  private skipExpression(): void {
    let depth = 0;
    let previous = '';

    while (!this.atEnd()) {
      this.skipTrivia();
      const char = this.peek();

      if (char === '') return;
      if (depth === 0 && (char === ',' || char === '}' || char === ']' || char === ')')) return;

      if (char === '{' || char === '[' || char === '(') {
        depth += 1;
      } else if (char === '}' || char === ']' || char === ')') {
        depth -= 1;
      } else if (char === '"' || char === "'" || char === '`') {
        this.skipQuoted(char);
        previous = char;
        continue;
      } else if (char === '/' && REGEX_CAN_FOLLOW.has(previous)) {
        this.skipRegex();
        previous = '/';
        continue;
      }

      previous = char;
      this.index += 1;
    }
  }

  private skipQuoted(quote: string): void {
    this.index += 1;

    while (!this.atEnd()) {
      const char = this.peek();
      this.index += 1;

      if (char === '\\') {
        this.index += 1;
        continue;
      }
      if (char === quote) return;
    }
  }

  private skipRegex(): void {
    this.index += 1;
    let inCharacterClass = false;

    while (!this.atEnd()) {
      const char = this.peek();
      this.index += 1;

      if (char === '\\') {
        this.index += 1;
        continue;
      }
      if (char === '\n') return; // unterminated — treat the line as the limit
      if (char === '[') inCharacterClass = true;
      else if (char === ']') inCharacterClass = false;
      else if (char === '/' && !inCharacterClass) break;
    }

    while (!this.atEnd() && IDENTIFIER_PART.test(this.peek())) {
      this.index += 1; // flags
    }
  }
}

function toCodePoint(digits: string): string {
  if (!/^[\da-fA-F]+$/.test(digits)) return '';
  const code = Number.parseInt(digits, 16);
  return code > 0x10ffff ? '' : String.fromCodePoint(code);
}

/** Parse the first object literal in `source`; throws if none is found or it's unterminated. */
export function parseJsObjectLiteral(
  source: string,
  warnings?: ImportWarnings,
): Record<string, unknown> {
  return new JsObjectScanner(source, warnings).read();
}
